import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router'
import { useGetStep, useLastPathSegment } from 'shared/hooks'
import { Box, Button, Divider, Dropdown, IconButton, layoutStore, Tooltip } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ChatbotRoutes } from 'entities/ExportRouters'
import { ChatbotInstructionType, ChatbotStatus } from 'entities/Chatbot'
import { StepsCommonLayout } from 'pages/chatbot/ui/StepsCommonLayout/StepsCommonLayout'
import { useChatbotContext } from 'pages/chatbot/context/chatbotContext'
import { type AgentBookAppointmentStepsRoutes } from 'pages/chatbot/pages/agentBookAppointment'
import { ChatbotDialogProvider, useChatbotDialogContext } from 'pages/chatbot/context'
import { AgentEditHeader } from 'pages/chatbot/ui/AgentEditHeader/AgentEditHeader'
import { AgentEditCustomTitle } from 'pages/chatbot/ui/AgentEditCustomTitle/AgentEditCustomTitle'
import { ChatbotStatusBadge } from 'pages/chatbot/ui'
import { ChatbotTemplate } from 'pages/chatbot/config'

const AgentBookAppointment = observer(() => {
  const { agentBookAppointmentStepsStore, agentNavigationStore } = useChatbotContext()
  const {
    getSettingsStep,
    agentNavigationStore: { getCurrentStep },
    getProgressInfoTitle,
    currentStepStore,
  } = agentBookAppointmentStepsStore
  const settingsStep = useGetStep<AgentBookAppointmentStepsRoutes>(getSettingsStep)
  const infoTitle = getProgressInfoTitle(useLastPathSegment())
  const { isViewXs } = layoutStore
  const {
    id,
    disableChatbot,
    agentBookAppointmentStepsStore: {
      settingAgentStore,
      isExistChanges,
      stepLoading,
      saveAgentBeforeTest,
      setAdvancedSettings,
      agentActivateStore,
    },
    loaded,
    locked,
    model: { isActive, status, type, name, setName },
    errors,
  } = useChatbotContext()
  const { state: payload } = useLocation()
  const template = ChatbotTemplate.get(type ?? ChatbotInstructionType.BookAppointment)
  const nameError = errors.getError('name')
  const { isAutoConvType, checkValidate } = settingAgentStore
  const { loading: dialogLoading, start } = useChatbotDialogContext()
  const isTestButtonDisabled = id ? stepLoading || dialogLoading || locked : stepLoading
  const isActivateStep = getCurrentStep === 'activate-agent'

  useEffect(() => {
    agentNavigationStore.setCurrentStep(settingsStep)
  }, [settingsStep])

  if (id && !loaded) return null

  const handleNameChange = (value: string) => {
    setName(value)
    errors.removeError('name')
  }

  const handleTestAgent = async () => {
    if (id) {
      if (isExistChanges) {
        const isSaved = await saveAgentBeforeTest({ id: Number(id) })
        if (!isSaved) return
      }

      if (!checkValidate()) return

      start(isAutoConvType)
    } else {
      await saveAgentBeforeTest()
    }
  }

  const handlePublishAgent = () => {
    agentActivateStore.handleActivateClickWithModal(id?.toString(), {
      isPublished: isActive,
      shouldRedirect: true,
    })
  }

  const handleSaveAgent = async () => {
    await currentStepStore.handleSaveDraft(id?.toString())
  }

  const renderActions = () => [
    {
      iconButtonComponent: (
        <Box display='flex' alignItems='center' gap='8px'>
          <Dropdown
            width={162}
            placement='bottom-start'
            icon={'moreHorizontal'}
            tooltipProps={{
              label: 'Show actions',
              placement: 'top-end',
            }}
            iconButtonProps={{
              fontSize: 20,
              variant: 'contained',
              size: 'medium',
              ariaLabel: 'Actions_showActions',
            }}
            items={[
              ...(isActive
                ? [
                    {
                      id: 1,
                      label: 'Disable agent',
                      onClick: disableChatbot,
                    },
                  ]
                : []),
              ...(isViewXs
                ? [
                    {
                      id: 3,
                      label: 'Test agent',
                      onClick: handleTestAgent,
                      disabled: isTestButtonDisabled,
                    },
                  ]
                : []),
              {
                id: 2,
                label: 'Change agent type',
                disabled: true,
              },
              ...(isViewXs
                ? [
                    {
                      id: 4,
                      label: 'Settings',
                      onClick: setAdvancedSettings,
                    },
                  ]
                : []),
            ]}
          />
          {!isViewXs && (
            <Button contained='secondary' text='Settings' onClick={setAdvancedSettings} />
          )}
          {!isActive && (
            <Button
              text={id ? 'Save changes' : 'Save as draft'}
              contained='secondary'
              onClick={handleSaveAgent}
              disabled={currentStepStore.disabled || !isExistChanges}
            />
          )}
          <Button
            text={isActive ? 'Publish changes' : 'Activate'}
            contained='primary'
            onClick={handlePublishAgent}
            disabled={
              isActive ? agentActivateStore.disabled : !isActivateStep || currentStepStore.disabled
            }
          />
          <Box display='flex' alignItems='center'>
            <Divider
              orientation='vertical'
              sx={{ height: '16px !important', marginRight: '8px' }}
            />
            <IconButton
              ariaLabel='close_setting'
              icon='close'
              variant='contained'
              color='tertiary'
              size='medium'
              onClick={() =>
                uiStore.changeRoute({
                  path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}`,
                })
              }
            />
          </Box>
        </Box>
      ),
    },
  ]

  return (
    <>
      <AgentEditHeader
        customTitle={
          <AgentEditCustomTitle
            template={template}
            name={name}
            setName={handleNameChange}
            error={nameError}
            isAutoFocus={payload?.duplicateId}
          />
        }
        rightTitleContent={
          status !== null ? (
            <Tooltip
              label={status === ChatbotStatus.Invalid && 'Suspicious content detected'}
              placement='top'
            >
              <ChatbotStatusBadge status={status} />
            </Tooltip>
          ) : null
        }
        actionsGap={8}
        actions={renderActions()}
      />
      <StepsCommonLayout progressTitle={infoTitle} totalSteps={4} settingsStep={settingsStep} />
    </>
  )
})

export const AgentBookAppointmentStepsLayout = observer(() => (
  <ChatbotDialogProvider>
    <AgentBookAppointment />
  </ChatbotDialogProvider>
))
